import { type FC, useCallback, useEffect } from "react";
import SearchPeriodSelector from "./SearchPeriodSelector/SearchPeriodSelector";
import SearchOrderStatusLineSelector from "./SearchOrderStatusLineSelector/SearchOrderStatusLineSelector";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { SearchCard } from "../../../../common/components/SearchCard";
import {
  isInvoiceNumberEnabled,
  isLineLevelCustomerOrderNumberEnabled,
  isMultiAccountOrderSearchEnabled,
} from "../../../../utils/features";
import { useDropdownDataProvider } from "./useDropdownDataProvider";
import { getDatesFromForm } from "../../../../ABMonitor/components/ABMonitorSearchBox/ABMonitorSearchBox";
import { PredefinedDate } from "../../../common/utils/predefinedDates";
import {
  makeSearchByValidator,
  QueryType,
  type SearchByData,
  SearchByInput,
} from "../../../../ABMonitor/components/ABMonitorSearchBox/components/SearchByInput/SearchByInput";
import { IconType } from "../../../../common/components/icon/IconType";
import { Button } from "../../../../common/components/Button/Button";
import { isDateValidForOrderSearch } from "../../../common/utils/IsDateValidForOrderSearch";
import { SearchBoxVariant } from "./SearchBoxVariant";
import { useLoadPersistedFormOnMount, useSavePersistedFormOnChange } from "./searchFormPersistenceUtils";

import classes from "./OrderListSearchBox.module.less";
import classNames from "classnames";
import { getInitialAccounts, MultiAccountSelect } from "./MultiAccountSelect/MultiAccountSelect";
import { type SingleAccountData } from "../../../common/DownloadOrderHistoryService/types";
import { useTranslation } from "react-i18next";

import { isNonNull } from "~/utils/reducers";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Alert } from "~/common/m3/Alert/Alert";

export interface OrderListSearchBoxProps {
  className?: string;
  onSubmit: (formData: OrderListSearchBoxData) => void;
  variant: SearchBoxVariant;
}

interface SearchFormData {
  chosenDate: { start: Date | undefined; end: Date | undefined };
  isCustomDate: boolean;
  isPredefinedDate: boolean;
  predefinedDate: PredefinedDate;

  searchBy: SearchByData;

  isLineStatus?: boolean;
  isStatus?: boolean;
  lineStatusOption?: string;
  statusOption?: string;

  accounts: string[];
}

const INITIAL_SEARCH_FORM_DATA: SearchFormData = {
  predefinedDate: PredefinedDate.LAST_30_DAYS,
  isCustomDate: false,
  isPredefinedDate: true,
  chosenDate: { start: undefined, end: undefined },

  isLineStatus: false,
  isStatus: true,

  searchBy: {
    query: "",
    type: QueryType.PURCHASE_NO,
  },
  accounts: getInitialAccounts(),
};

export interface OrderListSearchBoxData {
  query: string;
  queryType: QueryType;
  dateFrom?: Date;
  dateTo?: Date;
  isOrder: boolean;
  status?: string;
  lineStatus?: string;
  accounts?: SingleAccountData[];
}

const AVAILABLE_QUERY_TYPES = (variant: SearchBoxVariant) =>
  [
    QueryType.PURCHASE_NO,
    QueryType.SALES_NO,
    QueryType.DESIGNATION,
    QueryType.SAP_MATERIAL_NO,
    QueryType.CUSTOMER_MATERIAL_NO,
    isLineLevelCustomerOrderNumberEnabled() ? QueryType.CUSTOMER_ORDER_NO : undefined,
    isOrderList(variant) && isInvoiceNumberEnabled() ? QueryType.INVOICE_NO : undefined,
  ].filter(isNonNull);

export const OrderListSearchBox: FC<OrderListSearchBoxProps> = ({ className, onSubmit, variant }) => {
  const { t } = useTranslation();
  const formHooks = useForm<SearchFormData>({
    defaultValues: INITIAL_SEARCH_FORM_DATA,
    mode: "onBlur",
  });

  const hasErrors = Object.keys(formHooks.formState.errors).length > 0;

  const { orderStatuses, lineItemStatuses, loading: isDropdownDataLoading } = useDropdownDataProvider(variant);

  const storageKey = getStorageKey(variant);
  useLoadPersistedFormOnMount(storageKey, formHooks.reset);
  useSavePersistedFormOnChange(storageKey, formHooks.getValues);

  useEffect(() => {
    if (!isDropdownDataLoading) {
      formHooks.handleSubmit(onSubmitCb)();
    }
  }, [isDropdownDataLoading]);

  const onSubmitCb = useCallback(() => {
    const formData: SearchFormData = formHooks.getValues();
    const { searchBy, accounts: formAccounts } = formData;

    let accounts:
      | {
          accountNumber: string;
          endCustomer: string;
          salesOrg: string;
        }[]
      | undefined = undefined;
    if (formAccounts) {
      accounts = formAccounts
        .map((str) => str.split("-"))
        .map((parts) => ({
          accountNumber: parts[0] ?? "",
          endCustomer: parts[1] ?? "",
          salesOrg: parts[2] ?? "",
        }));
    }

    const requestPayload: OrderListSearchBoxData = {
      query: searchBy.query.trim(),
      queryType: searchBy.type,
      isOrder: true,
      status: formData.isLineStatus ? "" : formData.statusOption,
      lineStatus: formData.isLineStatus ? formData.lineStatusOption : "",
      ...getDatesFromForm(formData),
      accounts,
    };
    onSubmit(requestPayload);
  }, [onSubmit]);

  if (isDropdownDataLoading) {
    return null;
  }

  const pageVariant = isOrderList(variant) ? "order" : "quote";

  return (
    <FormProvider {...formHooks}>
      <SearchCard className={classNames(className, classes.card)}>
        <form onSubmit={formHooks.handleSubmit(onSubmitCb)}>
          <div className={classes.row}>
            <Controller
              render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
                <SearchByInput
                  className={classes.searchBy}
                  value={value}
                  onChange={onChange}
                  visibleOptions={AVAILABLE_QUERY_TYPES(variant)}
                  error={!!error}
                  helperLabel={error?.message}
                  onBlur={onBlur}
                  ref={ref}
                  pageVariant={pageVariant}
                />
              )}
              name={"searchBy"}
              defaultValue={INITIAL_SEARCH_FORM_DATA.searchBy}
              control={formHooks.control}
              rules={{
                validate: makeSearchByValidator(t, pageVariant),
              }}
            />

            <SearchPeriodSelector
              formHooks={formHooks}
              isDateValidForSelection={isDateValidForOrderSearch}
              className={classes.searchPeriod}
            />
          </div>

          <div className={classes.row}>
            <div className={classes.searchStatus}>
              <SearchOrderStatusLineSelector
                statusDropdown={orderStatuses}
                lineStatusDropdown={lineItemStatuses}
                formHooks={formHooks}
                variant={variant}
              />
            </div>

            {isMultiAccountOrderSearchEnabled() && (
              <div className={classes.accountSelect}>
                <Controller
                  render={({ field: { value, onChange } }) => <MultiAccountSelect values={value} onChange={onChange} />}
                  name={"accounts"}
                  defaultValue={INITIAL_SEARCH_FORM_DATA.accounts}
                  control={formHooks.control}
                />
              </div>
            )}
          </div>

          <div className={classes.buttonRow}>
            <Button
              variant={"text"}
              data-test-id={"search-form-reset"}
              startIcon={<DefaultButtonIcon type={IconType.Reload} />}
              onClick={() => {
                formHooks.reset(INITIAL_SEARCH_FORM_DATA);
                onSubmitCb();
              }}
            >
              {t("common.button.resetForm")}
            </Button>

            <Button
              disabled={window.SCHA.config.disableAllWS || hasErrors}
              type={"submit"}
              id={"orderlistpage_apply_btn"}
            >
              {t("searchPage.refinement.button.apply")}
            </Button>
          </div>
          {formHooks.watch("searchBy")?.type === QueryType.SALES_NO && (
            <Alert severity="info">{t("orderListPage.soInfo")}</Alert>
          )}
        </form>
      </SearchCard>
    </FormProvider>
  );
};

const STORAGE_KEY_MAP = {
  [SearchBoxVariant.ORDER]: "orderHistorySearchData",
  [SearchBoxVariant.QUOTE]: "quoteHistorySearchData",
};

const getStorageKey = (variant: SearchBoxVariant) => {
  return STORAGE_KEY_MAP[variant];
};

const isOrderList = (variant: SearchBoxVariant) => {
  return variant === SearchBoxVariant.ORDER;
};
